import React, { useState, useRef, useEffect } from 'react';
import { MemberSearchResult, RecentUser } from '../../types/Member';
import { memberService } from '../../services/memberService';

interface MemberSearchProps {
  onSelectMember: (member: MemberSearchResult) => void;
  onClose?: () => void;
  excludeUids?: string[];
  placeholder?: string;
}

export const MemberSearch: React.FC<MemberSearchProps> = ({
  onSelectMember,
  onClose,
  excludeUids = [],
  placeholder = "Search users to add..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent users on mount
  useEffect(() => {
    const loadRecentUsers = async () => {
      setIsLoadingRecent(true);
      try {
        const recent = await memberService.getRecentUsers(excludeUids);
        setRecentUsers(recent);
      } catch (err) {
        console.error('Failed to load recent users:', err);
      } finally {
        setIsLoadingRecent(false);
      }
    };

    loadRecentUsers();
  }, [excludeUids]);

  // Note: Outside click handling is now managed by the modal backdrop in MemberManagerV2

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Search users with debouncing
  useEffect(() => {
    const searchUsers = async () => {
      if (searchTerm.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const results = await memberService.searchUsers(searchTerm, excludeUids);
        setSearchResults(results);
      } catch (err: any) {
        setError(err.message || 'Search failed');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchUsers, 300); // 300ms debounce
    return () => clearTimeout(timeoutId);
  }, [searchTerm, excludeUids]);

  const handleSelectMember = (member: MemberSearchResult) => {
    onSelectMember(member);
    setSearchTerm('');
    setSearchResults([]);
  };

  const showRecentUsers = !searchTerm && recentUsers.length > 0;
  const showSearchResults = searchTerm.length >= 2 && searchResults.length > 0;
  const showNoResults = searchTerm.length >= 2 && !isSearching && searchResults.length === 0;

  return (
    <div ref={containerRef} style={{
      width: '280px', // Fixed width instead of 100%
      position: 'relative'
    }}>
      {/* Search Input */}
      <div style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center'
      }}>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #e0e7ff',
            borderRadius: '8px',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#667eea';
            e.target.style.boxShadow = '0 0 0 3px rgba(103, 126, 234, 0.1)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e0e7ff';
            e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
          }}
        />
        {isSearching && (
          <div style={{
            position: 'absolute',
            right: '12px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #f3f4f6',
              borderTop: '2px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          marginTop: '8px',
          padding: '8px 12px',
          backgroundColor: '#fef2f2',
          color: '#dc2626',
          fontSize: '12px',
          borderRadius: '6px',
          border: '1px solid #fecaca'
        }}>
          {error}
        </div>
      )}

      {/* Results Dropdown */}
      {(showRecentUsers || showSearchResults || showNoResults) && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #e0e7ff',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          zIndex: 50,
          maxHeight: '300px',
          overflowY: 'auto',
          marginTop: '4px'
        }}>
          {/* Recent Users Section */}
          {showRecentUsers && (
            <>
              <div style={{
                padding: '8px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderBottom: '1px solid #f3f4f6'
              }}>
                Recent Users
              </div>
              {isLoadingRecent ? (
                <div style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '12px'
                }}>
                  Loading recent users...
                </div>
              ) : (
                recentUsers.map((user) => (
                  <UserItem
                    key={user.uid}
                    user={user}
                    onClick={() => handleSelectMember(user)}
                  />
                ))
              )}
            </>
          )}

          {/* Search Results Section */}
          {showSearchResults && (
            <>
              {showRecentUsers && (
                <div style={{
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#6b7280',
                  backgroundColor: '#f9fafb',
                  borderBottom: '1px solid #f3f4f6',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  Search Results
                </div>
              )}
              {searchResults.map((user) => (
                <UserItem
                  key={user.uid}
                  user={user}
                  onClick={() => handleSelectMember(user)}
                />
              ))}
            </>
          )}

          {/* No Results Message */}
          {showNoResults && (
            <div style={{
              padding: '16px 12px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '13px'
            }}>
              No users found matching "{searchTerm}"
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// User Item Component
interface UserItemProps {
  user: MemberSearchResult;
  onClick: () => void;
}

const UserItem: React.FC<UserItemProps> = ({ user, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '10px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f3f4f6',
        transition: 'background-color 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f8fafc';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {/* Avatar */}
      {user.photoURL ? (
        <img
          src={user.photoURL}
          alt={user.displayName}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            objectFit: 'cover',
            border: '1px solid #e5e7eb'
          }}
        />
      ) : (
        <div style={{
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          backgroundColor: '#667eea',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: '600',
          color: 'white'
        }}>
          {user.displayName.charAt(0).toUpperCase()}
        </div>
      )}

      {/* User Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '500',
          color: '#1f2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {user.displayName}
        </div>
        <div style={{
          fontSize: '12px',
          color: '#6b7280',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {user.email}
          {user.username && ` • @${user.username}`}
        </div>
      </div>
    </div>
  );
};